import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Loader, CreditCard } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { cardsAPI } from '../services/api';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('success');
  const [cardId, setCardId] = useState(null);

  useEffect(() => {
    // Stripe checkout sessions are no longer used — cards are purchased
    // directly via Firebase Functions (purchase_card / purchase_card_with_crypto).
    // This page now just confirms success and fetches the most recently created card.
    fetchLatestCard();
  }, []);

  const fetchLatestCard = async () => {
    try {
      const response = await cardsAPI.getMyCards();
      const cards = response.data;

      if (cards && cards.length > 0) {
        // Find the most recently created card (within last 2 minutes)
        const recentCard = cards.find(card =>
          card.created_at && new Date(card.created_at) > new Date(Date.now() - 120000)
        );
        if (recentCard) {
          setCardId(recentCard.id);
        } else {
          // Fall back to the most recent card overall
          const sorted = [...cards].sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
          setCardId(sorted[0]?.id);
        }
      }

      setStatus('success');
      toast.success('Payment successful! Your card has been created.');
    } catch (error) {
      console.error('Failed to fetch card:', error);
      setStatus('error');
      toast.error('Payment processed but could not load card. Check your cards page.');
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass-card p-12 rounded-2xl text-center"
        >
          {status === 'checking' && (
            <>
              <Loader className="w-16 h-16 text-[#7000FF] mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-primary font-bold text-white mb-4">VERIFYING PAYMENT</h1>
              <p className="text-gray-400 font-secondary">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-[#00FF94] mx-auto mb-6" />
              <h1 className="text-3xl font-primary font-bold text-white mb-4">PAYMENT SUCCESSFUL!</h1>
              <p className="text-gray-400 font-secondary mb-8">Your card has been created and is ready to use.</p>

              <div className="flex gap-4 justify-center">
                {cardId && (
                  <Button
                    onClick={() => navigate(`/card/${cardId}`)}
                    className="bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full px-8"
                    data-testid="view-card-btn"
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    View Card
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/cards')}
                  className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-full px-8"
                  data-testid="back-to-cards-btn"
                >
                  Back to Cards
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-full bg-[#FF0080]/20 flex items-center justify-center mx-auto mb-6">
                <span className="text-[#FF0080] text-4xl">!</span>
              </div>
              <h1 className="text-3xl font-primary font-bold text-white mb-4">PAYMENT ISSUE</h1>
              <p className="text-gray-400 font-secondary mb-8">
                There was an issue loading your card. Please check your cards page —
                your card may already be there.
              </p>
              <Button
                onClick={() => navigate('/cards')}
                className="bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full px-8"
                data-testid="back-to-cards-error-btn"
              >
                Back to Cards
              </Button>
            </>
          )}
        </motion.div>
      </div>
    </Layout>
  );
};

export default PaymentSuccess;